import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { appointmentService } from '../services/appointmentService';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';

const Appointments = () => {
  const { isChimney } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const data = await appointmentService.getAll();
        setAppointments(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setError('Nie udało się załadować wizyt');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Czy na pewno chcesz anulować tę wizytę?')) {
      return;
    }

    try {
      const updated = await appointmentService.cancel(id);
      setAppointments(appointments.map((apt) => (apt.id === id ? updated : apt)));
      alert('Wizyta została anulowana');
    } catch (err) {
      alert('Nie udało się anulować wizyty');
    }
  };

  const handleStatusAction = async (id, actionName) => {
    try {
      const updated =
        actionName === 'confirm'
          ? await appointmentService.confirm(id)
          : await appointmentService.complete(id);
      setAppointments(appointments.map((apt) => (apt.id === id ? updated : apt)));
    } catch (err) {
      alert(err.response?.data?.detail || 'Nie udało się zmienić statusu wizyty');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-ik-orange text-xl">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-ik-black mb-8">
            {isChimney() ? 'Wizyty kominiarza' : 'Moje wizyty'}
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {appointments.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <p className="text-gray-600 text-lg mb-4">
                Nie masz jeszcze umówionych wizyt.
              </p>
              <Link
                to="/chimneys"
                className="inline-block px-6 py-2 bg-ik-orange text-white rounded-lg hover:bg-orange-600 transition"
              >
                Znajdź kominiarza
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold text-ik-black mb-2">
                        {isChimney()
                          ? `${appointment.user?.first_name || ''} ${appointment.user?.last_name || ''}`.trim() || 'Klient'
                          : appointment.chimney?.company_name || 'Kominiarz'}
                      </h3>
                      <p className="text-gray-600 mb-1">
                        <span className="font-medium">Data:</span>{' '}
                        {formatDate(appointment.appointment_date)}
                      </p>
                      {appointment.chimney?.city && (
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Miasto:</span>{' '}
                          {appointment.chimney.city}
                        </p>
                      )}
                      {isChimney() && appointment.user?.phone && (
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Telefon klienta:</span>{' '}
                          {appointment.user.phone}
                        </p>
                      )}
                      <p className="text-gray-600">
                        <span className="font-medium">Status:</span>{' '}
                        <span
                          className={`font-semibold ${
                            appointment.status === 'confirmed'
                              ? 'text-green-600'
                              : appointment.status === 'cancelled'
                              ? 'text-red-600'
                              : 'text-yellow-600'
                          }`}
                        >
                          {appointment.status === 'confirmed'
                            ? 'Potwierdzona'
                            : appointment.status === 'cancelled'
                            ? 'Anulowana'
                            : appointment.status === 'completed'
                            ? 'Zakończona'
                            : 'Oczekująca'}
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      {isChimney() && appointment.status === 'pending' && (
                        <button
                          onClick={() => handleStatusAction(appointment.id, 'confirm')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                        >
                          Potwierdź
                        </button>
                      )}
                      {isChimney() &&
                        appointment.status !== 'completed' &&
                        appointment.status !== 'cancelled' && (
                          <button
                            onClick={() => handleStatusAction(appointment.id, 'complete')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                          >
                            Zakończ
                          </button>
                        )}
                      {appointment.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancel(appointment.id)}
                          className="px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition text-sm"
                        >
                          Anuluj
                        </button>
                      )}
                      {!isChimney() && appointment.status === 'completed' && !appointment.review && (
                        <Link
                          to={`/review/${appointment.id}`}
                          className="px-4 py-2 bg-ik-orange text-white rounded-lg hover:bg-orange-600 transition text-sm text-center"
                        >
                          Wystaw opinię
                        </Link>
                      )}
                      {!isChimney() && (
                        <Link
                          to={`/chimney/${appointment.chimney?.id}`}
                          className="px-4 py-2 border-2 border-ik-orange text-ik-orange rounded-lg hover:bg-ik-orange hover:text-white transition text-sm text-center"
                        >
                          Zobacz profil
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Appointments;
